/*
 * <returns-portal>: the in-house returns and exchanges page.
 *
 * Screens: lookup -> units (tick one or more) -> unit, once per ticked unit (reason, then
 * return or exchange) -> the exchange targets, one tab per exchanged unit (size, colour
 * or another product) -> summary -> done.
 * Data, all fetched in the background so the customer never leaves the site:
 *   - order + units: POST {order_name, phone} or {order_name, email} to the Supabase
 *     returns-lookup function (the customer picks phone or email on the first screen);
 *     from the account page (?from=account) an account token instead, which the orders table got
 *     from the returns app's App Proxy (Supabase returns-account, assets/account-returns.js) and
 *     left in this tab's sessionStorage
 *   - sizes/colours: /products/{handle}?section_id=returns-offers   (Section Rendering API)
 *   - price/submit:  POST to returns-submit, modes quote | token | submit
 * "Another product" is picked by browsing the site in exchange mode
 * (snippets/exchange-mode.liquid, window.WeShoesRx); the cart is then read here
 * at #rx-summary and priced by the server. Amounts shown always come from the server.
 * Customer-facing copy comes from the section's [data-strings] block.
 */
(() => {
  if (customElements.get('returns-portal')) return;

  // Build DOM nodes without innerHTML: product titles are data, never markup.
  function h(tag, props = {}, ...kids) {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(props)) {
      if (value == null || value === false) continue;
      if (key === 'class') node.className = value;
      else if (key === 'text') node.textContent = value;
      else node.setAttribute(key, value === true ? '' : String(value));
    }
    for (const kid of kids.flat()) {
      if (kid == null || kid === false) continue;
      node.append(kid instanceof Node ? kid : document.createTextNode(String(kid)));
    }
    return node;
  }

  const money = (amount) => {
    const n = Number(amount);
    return Number.isFinite(n) ? n.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' }) : '';
  };
  const fromAgorot = (agorot) => money(Number(agorot) / 100);
  // A credit, shown with a minus: "-249.90₪". The locale's direction marks are dropped, or
  // the minus lands after the number in a right-to-left line; the space goes too, so it
  // lines up with the other amounts.
  const negative = (amount) => `-${money(amount).replace(/[\u200e\u200f\s]/g, '')}`;
  const fmtDate = (iso) => new Date(iso).toLocaleDateString('he-IL');
  const fmtDateTime = (iso) => new Date(iso).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' });

  function sizedImage(src, width) {
    if (!src) return null;
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  }

  const ERRORS = {
    not_found: 'errNotFound',
    bad_input: 'errBadInput',
    not_eligible: 'errNotEligible',
    not_delivered: 'errNotDelivered',
    not_enabled: 'errNotEnabled',
    target_unavailable: 'errUnavailable',
    target_not_in_family: 'errUnavailable',
    unit_not_returnable: 'errUnitTaken',
    token_expired: 'errExpired',
    bad_token: 'errExpired',
    address_required: 'errAddress',
    refund_window_closed: 'errWindow',
    exchange_window_closed: 'errWindow',
    cart_empty: 'rxEmpty',
    cart_too_big: 'errCartBig',
    pending_request_exists: 'errPending',
    quote_changed: 'errQuoteChanged',
    cancel_failed: 'errCancel',
    branch_returns_only: 'errBranchReturnsOnly',
    branch_window_closed: 'errBranchWindow',
    code_already_used: 'errCodeUsed',
    code_failed: 'errCode',
  };

  // The reason a request from the branch QR page carries (the page asks none). returns-submit
  // accepts it for branch returns only.
  const QR_REASON = 'החזרה בסניף (סריקת QR)';

  // Where the orders table in the account leaves the token of the order clicked (assets/account-returns.js).
  const ACCOUNT_TOKEN_KEY = 'ws-returns-account';

  class ReturnsPortal extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
      this.lookupUrl = this.dataset.lookupUrl;
      this.submitUrl = this.dataset.submitUrl;
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      // The branch QR page (templates/page.branch-return.json): items, confirm, code.
      this.branchOnly = this.dataset.mode === 'branch';
      this.state = {
        auth: null,             // { order_name, phone }, { order_name, email } or { account_token } after a lookup
        idBy: 'phone',          // how the customer identifies the order: 'phone' or 'email'
        order: null,
        windows: null,
        method: 'courier',      // how the units come back: 'courier' (fee) or 'branch' (free)
        quoteSeq: 0,            // only the newest quote request is drawn
        branchPending: [],      // branch returns still waiting for the customer
        history: [],            // units already exchanged or returned, with the date
        units: [],
        picks: new Map(),       // unit_key -> { reason, action, target }
        selected: new Set(),    // unit_keys ticked on the units screen
        wizard: [],             // the ticked units, in order, walked one by one
        wizIndex: 0,
        draft: null,            // the pick being edited on the unit screen
        unitKey: null,
        step: 'reason',
        targets: new Map(),     // id -> target object behind a size button
        targetSeq: 0,
        offers: new Map(),      // product handle -> returns-offers JSON
        quoteOk: false,
        payNow: null,           // the amount the customer was shown, sent back on submit
        pending: null,          // an unpaid request already sent for this order
        rx: null,               // { token, cart } when summarising after browsing
        busy: false,
        branch: null,           // the branch QR page: { number, name } the customer returns at
      };
      this.bind();
      if (this.branchOnly) this.fillBranches();
      this.setProgress('lookup');
      const fromAccount = new URLSearchParams(window.location.search).get('from') === 'account';
      if (window.location.hash === '#rx-summary' && window.WeShoesRx?.read()) {
        this.removeAttribute('data-from-account');
        this.rxSummary();
      } else if (fromAccount) this.fromAccount();
      else this.removeAttribute('data-from-account');
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.waitTimer);
    }

    // ---------------- the branch QR page: which branch ----------------

    // The branches, by name (and city, for the search), with the branch number as the value. The
    // JSON in the section shows at once; then the list the server keeps (returns-submit mode
    // "branches": public.returns_branches, kept daily from Stockist and Priority) replaces it.
    // When the server does not answer, the JSON stays.
    fillBranches() {
      const search = this.$('[data-branch-search]');
      if (!search || this.branches) return;
      let all = [];
      try {
        all = JSON.parse(this.$('[data-branches-json]')?.textContent || '[]');
      } catch (e) {
        all = [];
      }
      this.branches = this.sortBranches(all);
      search.disabled = this.branches.length === 0;
      if (this.branches.length) this.bindBranchSearch(search);
      this.post(this.submitUrl, { mode: 'branches' })
        .then((data) => {
          const fresh = data && data.ok ? this.sortBranches(data.branches) : [];
          if (!fresh.length) return;
          const wasEmpty = !this.branches.length;
          this.branches = fresh;
          search.disabled = false;
          if (wasEmpty) this.bindBranchSearch(search);
        })
        .catch(() => {})
        .finally(() => {
          if (!this.branches.length) this.fail(this.t('errBranchList'));
        });
    }

    // Sorted by the name after the brand, so "WeShoes -", "We Shoes -", "WeBags -" and "Crocs -" mix in one order.
    sortBranches(list) {
      const key = (name) => name.replace(/^\s*(we\s*(shoes|shos|bags)|crocs(\s*outlet)?)\s*-?\s*/i, '');
      return (Array.isArray(list) ? list : [])
        .filter((b) => b && b.number && b.name)
        .map((b) => ({ number: String(b.number).trim(), name: String(b.name).trim(), city: String(b.city || '').trim() }))
        .sort((a, b) => key(a.name).localeCompare(key(b.name), 'he'));
    }

    // Typing narrows the list under the box; a click, or the arrows and Enter, pick a branch.
    bindBranchSearch(search) {
      const { signal } = this.abort;
      const list = this.$('[data-branch-options]');
      const value = this.$('[data-branch-value]');
      const norm = (s) => String(s).toLowerCase().replace(/["'׳״\-]/g, ' ').replace(/\s+/g, ' ').trim();
      // For the search, also without י and ו: "דיזנגוף" finds "דיזינגוף".
      const loose = (s) => norm(s).replace(/[יו]/g, '');
      let shown = [];
      let active = -1;

      const mark = () => {
        [...list.children].forEach((li, i) => li.setAttribute('aria-selected', String(i === active)));
        const li = list.children[active];
        if (li?.id) { search.setAttribute('aria-activedescendant', li.id); li.scrollIntoView({ block: 'nearest' }); }
        else search.removeAttribute('aria-activedescendant');
      };
      const open = () => {
        const q = loose(search.value);
        shown = this.branches.filter((b) => !q || loose(`${b.name} ${b.city}`).includes(q));
        active = -1;
        list.replaceChildren(...(shown.length
          ? shown.map((b, i) => h('li', { id: `${list.id}-${i}`, role: 'option', 'aria-selected': 'false', 'data-index': i, text: b.name }))
          : [h('li', { class: 'returns-portal__combo-empty', text: 'לא נמצא סניף בשם הזה' })]));
        list.hidden = false;
        search.setAttribute('aria-expanded', 'true');
        // On a phone the keyboard takes the bottom half: bring the box up so the list fits below it.
        if (window.matchMedia('(max-width: 749px)').matches) search.scrollIntoView({ block: 'start' });
      };
      const close = () => {
        list.hidden = true;
        search.setAttribute('aria-expanded', 'false');
        search.removeAttribute('aria-activedescendant');
      };
      const choose = (b) => {
        value.value = b.number;
        search.value = b.name;
        this.state.branch = { number: b.number, name: b.name };
        this.$('[data-error]').hidden = true;
        close();
      };

      search.addEventListener('focus', open, { signal });
      search.addEventListener('input', () => {
        value.value = '';
        this.state.branch = null;
        open();
      }, { signal });
      search.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (list.hidden) open();
          if (!shown.length) return;
          active = (active + (e.key === 'ArrowDown' ? 1 : -1) + shown.length) % shown.length;
          mark();
        } else if (e.key === 'Enter' && !list.hidden) {
          // Enter picks the highlighted branch (or the only one left) instead of sending the form.
          const b = shown[active] ?? (shown.length === 1 ? shown[0] : null);
          if (b) { e.preventDefault(); choose(b); }
        } else if (e.key === 'Escape') {
          close();
        }
      }, { signal });
      // mousedown, not click: it comes before the box loses focus and closes the list.
      list.addEventListener('mousedown', (e) => {
        const li = e.target.closest('li[data-index]');
        if (!li) return;
        e.preventDefault();
        choose(shown[Number(li.dataset.index)]);
      }, { signal });
      search.addEventListener('blur', () => {
        // A name typed in full counts as picked.
        const exact = this.branches.find((b) => norm(b.name) === norm(search.value));
        if (exact) choose(exact); else close();
      }, { signal });
    }

    $(selector) { return this.querySelector(selector); }
    $$(selector) { return [...this.querySelectorAll(selector)]; }

    t(key, vars) {
      let s = this.strings[key] ?? key;
      if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(v);
      return s;
    }

    errText(code) { return this.t(ERRORS[code] || 'errServer'); }
    announce(message) { this.$('[data-status]').textContent = message; }

    bind() {
      const { signal } = this.abort;
      this.$('[data-lookup-form]').addEventListener('submit', (e) => { e.preventDefault(); this.lookup(); }, { signal });
      this.$('[data-declare]').addEventListener('change', () => this.updateButtons(), { signal });
      this.$('[data-condition]')?.addEventListener('change', () => this.updateUnitsButton(), { signal });
      this.$('[data-units]').addEventListener('change', (e) => {
        const box = e.target.closest('input[data-select]');
        if (!box) return;
        const key = box.dataset.select;
        if (box.checked) this.state.selected.add(key);
        else { this.state.selected.delete(key); this.state.picks.delete(key); }
        box.closest('.returns-portal__unit')?.classList.toggle('is-picked', box.checked);
        this.updateUnitsButton();
      }, { signal });
      this.$('[data-method-row]').addEventListener('change', (e) => {
        if (e.target.name !== 'returns-method') return;
        this.state.method = e.target.value === 'branch' ? 'branch' : 'courier';
        this.renderSummary();
      }, { signal });
      this.addEventListener('click', (e) => this.onClick(e), { signal });
    }

    onClick(event) {
      const t = event.target.closest('button');
      if (!t || !this.contains(t) || t.disabled) return;
      if (t.dataset.idTab) return this.switchId(t.dataset.idTab);
      if (t.dataset.go) return this.go(t.dataset.go);
      if (t.hasAttribute('data-start-wizard')) return this.branchOnly ? this.submitBranch(t) : this.startWizard();
      if (t.dataset.productTab) return this.openTargets(t.dataset.productTab);
      if (t.dataset.reason) return this.onReason(t.dataset.reason);
      if (t.dataset.action) return this.onAction(t.dataset.action);
      if (t.dataset.tab) return this.switchTab(t.dataset.tab);
      if (t.dataset.pick) return this.onPick(t.dataset.pick);
      if (t.hasAttribute('data-other')) return this.openRxDialog();
      if (t.hasAttribute('data-rx-go')) return this.rxGo();
      if (t.hasAttribute('data-rx-close')) return this.$('[data-rx-dialog]').close();
      if (t.hasAttribute('data-unit-back')) return this.unitBack();
      if (t.hasAttribute('data-summary-back')) return this.summaryBack();
      if (t.hasAttribute('data-targets-next')) return this.targetsNext();
      if (t.hasAttribute('data-start-rx')) return this.startRx();
      if (t.hasAttribute('data-back-shop')) return window.location.assign(this.root);
      if (t.hasAttribute('data-pending-cancel')) return this.cancelPending();
      if (t.dataset.branchCancel) return this.cancelBranch(t);
      if (t.hasAttribute('data-branch-back')) return this.openBranchBack();
      if (t.hasAttribute('data-branch-back-close')) return this.$('[data-branch-back-dialog]').close();
      if (t.hasAttribute('data-branch-back-go')) return this.branchBack(t);
      if (t.dataset.branchCode) return this.showCode(t);
      if (t.hasAttribute('data-submit')) return this.submit();
    }

    // The progress steps follow the screen shown, forwards and back: the steps before it done,
    // its own current, and the line filled up to it.
    setProgress(name) {
      const items = this.$$('[data-step-of]');
      const at = items.findIndex((li) => li.dataset.stepOf === name);
      if (at < 0) return;
      items.forEach((li, i) => {
        li.classList.toggle('is-done', i < at || name === 'done');
        li.classList.toggle('is-current', i === at && name !== 'done');
        if (i === at) li.setAttribute('aria-current', 'step'); else li.removeAttribute('aria-current');
      });
      this.$('[data-steps]').style.setProperty('--p', String(at / (items.length - 1)));
    }

    show(name) {
      this.setProgress(name);
      this.$$('[data-screen]').forEach((s) => { s.hidden = s.dataset.screen !== name; });
      const screen = this.$(`[data-screen="${name}"]`);
      const focusTarget = screen.querySelector('h2, .returns-portal__lead, input');
      if (focusTarget) {
        if (!focusTarget.matches('input')) focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
      }
      this.scrollIntoView({ block: 'start' });
    }

    go(name) {
      if (name === 'summary') this.renderSummary();
      if (name === 'units') this.renderUnits();
      this.show(name);
    }

    async post(url, payload) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: this.abort.signal,
      });
      return res.json().catch(() => ({ ok: false, error: 'server_error' }));
    }

    // ---------------- lookup ----------------

    // Phone or email: one field shown at a time.
    switchId(by) {
      this.state.idBy = by === 'email' ? 'email' : 'phone';
      this.$$('[data-id-tab]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.idTab === this.state.idBy)));
      this.$$('[data-id-field]').forEach((f) => { f.hidden = f.dataset.idField !== this.state.idBy; });
      this.$('[data-error]').hidden = true;
      this.$(`[data-id-field="${this.state.idBy}"] input`).focus();
    }

    async lookup() {
      const form = this.$('[data-lookup-form]');
      const button = this.$('[data-lookup-submit]');
      const byPhone = this.state.idBy === 'phone';
      const orderName = form.elements.order_name.value.trim();
      const id = (byPhone ? form.elements.phone : form.elements.email).value.trim();
      this.$('[data-error]').hidden = true;
      const idOk = byPhone ? id.replace(/\D/g, '').length >= 9 : id.includes('@');
      if (!orderName || !idOk) return this.fail(this.t(byPhone ? 'errBadInputPhone' : 'errBadInput'));
      // The branch is set when one is picked from the list (bindBranchSearch); typed text alone is not.
      if (this.branchOnly && !this.state.branch?.number) return this.fail(this.t('errBranchPick'));

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        const auth = byPhone ? { order_name: orderName, phone: id } : { order_name: orderName, email: id };
        const data = await this.post(this.lookupUrl, auth);
        if (!data.ok) return this.lookupFail(data, byPhone);
        const closed = this.closedText(data);
        if (closed) return this.fail(closed);
        this.state.auth = auth;
        this.applyLookup(data);
        this.go('units');
      } catch (e) {
        if (e.name !== 'AbortError') this.fail(this.t('errServer'));
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    lookupFail(data, byPhone) {
      if (data.error === 'not_delivered') return this.fail(this.t(data.source === 'branch' ? 'errNotDeliveredBranch' : 'errNotDelivered'));
      if (byPhone && data.error === 'not_found') return this.fail(this.t('errNotFoundPhone'));
      if (byPhone && data.error === 'bad_input') return this.fail(this.t('errBadInputPhone'));
      return this.fail(this.errText(data.error));
    }

    // Every window closed and nothing left to finish (an unpaid request, a branch code): say so
    // on the first screen instead of opening a list nothing can be picked from (owner, 2026-10-19).
    closedText(data) {
      const w = data.windows || {};
      const open = this.branchOnly ? w.refund : w.refund || w.exchange;
      if (open || data.pending || (data.branch_pending || []).length) return null;
      return this.t(this.branchOnly ? 'qrRefundClosed' : 'errClosed');
    }

    // From the orders table in the account (?from=account): the order comes as an account token,
    // which the orders table got from the returns app's App Proxy (Shopify vouched for the
    // logged-in customer, the server checked the order is theirs) and left in this tab's
    // sessionStorage. Nothing in the address says which order, so there is nothing to change
    // there. No token, an expired one, or a failure leaves the form.
    async fromAccount() {
      const button = this.$('[data-lookup-submit]');
      let token = null;
      try { token = window.sessionStorage.getItem(ACCOUNT_TOKEN_KEY); } catch { /* storage blocked: the form */ }
      if (!token) {
        this.removeAttribute('data-from-account');
        return this.fail(this.t('errAccount'));
      }
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.announce(this.t('accountLoading'));
      try {
        const auth = { account_token: token };
        const data = await this.post(this.lookupUrl, auth);
        if (!data.ok) return data.error === 'not_delivered' ? this.lookupFail(data, false) : this.fail(this.t('errAccount'));
        const closed = this.closedText(data);
        if (closed) return this.fail(closed);
        this.state.auth = auth;
        this.applyLookup(data);
        this.go('units');
      } catch (e) {
        if (e.name !== 'AbortError') this.fail(this.t('errAccount'));
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        this.removeAttribute('data-from-account');  // the loading line goes; the form only if still on it
      }
    }

    applyLookup(data) {
      this.state.order = data.order;
      this.state.windows = data.windows;
      this.state.units = data.units || [];
      this.state.pending = data.pending || null;
      this.state.history = data.history || [];
      this.state.branchPending = data.branch_pending || [];
      this.state.method = 'courier';
      this.state.picks.clear();
      // One returnable unit: already ticked, one tap to go on.
      this.state.selected = new Set(this.state.units.length === 1 ? [this.state.units[0].unit_key] : []);
    }

    // Drop an unpaid request (draft + Return) and reload the order's units.
    async cancelPending() {
      const button = this.$('[data-pending-cancel]');
      button.disabled = true;
      try {
        const data = await this.post(this.submitUrl, { mode: 'cancel', ...this.state.auth });
        if (!data.ok) return this.announceIn('[data-pending-text]', this.errText(data.error));
        const fresh = await this.post(this.lookupUrl, this.state.auth);
        if (!fresh.ok) return this.announceIn('[data-pending-text]', this.errText(fresh.error));
        this.applyLookup(fresh);
        this.go('units');
        this.announceIn('[data-window-note]', this.t('pendingCancelled'));
      } catch (e) {
        if (e.name !== 'AbortError') this.announceIn('[data-pending-text]', this.t('errServer'));
      } finally {
        button.disabled = false;
      }
    }

    // Cancel a branch return the customer no longer wants, then reload the order.
    async cancelBranch(button) {
      button.disabled = true;
      try {
        const data = await this.post(this.submitUrl, { mode: 'cancel', ...this.state.auth, return_id: button.dataset.branchCancel });
        if (!data.ok) return this.announceIn('[data-window-note]', this.errText(data.error));
        const fresh = await this.post(this.lookupUrl, this.state.auth);
        if (!fresh.ok) return this.announceIn('[data-window-note]', this.errText(fresh.error));
        this.applyLookup(fresh);
        this.go('units');
        this.announceIn('[data-window-note]', this.t('branchCancelled'));
      } catch (e) {
        if (e.name !== 'AbortError') this.announceIn('[data-window-note]', this.t('errServer'));
      } finally {
        button.disabled = false;
      }
    }

    // Back to the items from the code screen (owner, 2026-11-03): the same cancel as above (the
    // code first, then the Return, refused once the branch took the code), then the order's
    // units again. The wait for the cashier stops; if the cancel fails it starts again.
    openBranchBack() {
      this.$('[data-branch-back-error]').hidden = true;
      this.$('[data-branch-back-dialog]').showModal();
    }

    async branchBack(button) {
      const id = this.state.doneReturnId;
      const error = this.$('[data-branch-back-error]');
      if (!id) return;
      error.hidden = true;
      button.disabled = true;
      clearTimeout(this.waitTimer);
      try {
        const data = await this.post(this.submitUrl, { mode: 'cancel', ...this.state.auth, return_id: id });
        if (!data.ok) {
          this.waitForBranch(id);
          return this.announceIn('[data-branch-back-error]', this.errText(data.error));
        }
        const fresh = await this.post(this.lookupUrl, this.state.auth);
        if (!fresh.ok) return this.announceIn('[data-branch-back-error]', this.errText(fresh.error));
        this.$('[data-branch-back-dialog]').close();
        this.$('[data-branch-wait]').hidden = true;
        this.$('[data-branch-back]').hidden = true;
        this.$('[data-done-branch]').hidden = true;
        this.state.doneReturnId = null;
        this.applyLookup(fresh);
        this.go('units');
        this.announceIn('[data-window-note]', this.t('branchBackDone'));
      } catch (e) {
        if (e.name !== 'AbortError') {
          this.waitForBranch(id);
          this.announceIn('[data-branch-back-error]', this.t('errServer'));
        }
      } finally {
        button.disabled = false;
      }
    }

    // The code to show the cashier: the live one, or a new one once it expired (12 hours).
    async showCode(button) {
      button.disabled = true;
      try {
        const data = await this.post(this.submitUrl, { mode: 'code', ...this.state.auth, return_id: button.dataset.branchCode });
        if (!data.ok) return this.announceIn('[data-window-note]', this.errText(data.error));
        button.replaceWith(this.codeBox(data));
        this.announce(this.t('branchCodeUntil', { when: fmtDateTime(data.expires_at) }));
      } catch (e) {
        if (e.name !== 'AbortError') this.announceIn('[data-window-note]', this.t('errServer'));
      } finally {
        button.disabled = false;
      }
    }

    codeBox(c) {
      return h('div', { class: 'returns-portal__code-box' },
        h('p', { class: 'returns-portal__code', dir: 'ltr', text: c.code }),
        h('p', { class: 'returns-portal__code-until', text: this.t('branchCodeUntil', { when: fmtDateTime(c.expires_at) }) }));
    }

    // What to show the cashier: the 6-digit code (right after the request), or a button
    // that fetches it (when the customer comes back later), then the order and the items.
    branchCard(b) {
      const howTo = this.branchOnly ? this.t('qrInstructions') : this.t('branchInstructions', { date: fmtDate(b.until) });
      return h('div', { class: 'returns-portal__branch' },
        h('p', { class: 'returns-portal__notice', text: howTo }),
        b.code ? this.codeBox(b)
          : h('button', { type: 'button', class: 'button returns-portal__primary', 'data-branch-code': b.return_id, text: this.t('branchShowCode') }),
        h('p', { class: 'returns-portal__meta', text: this.t('orderLine', { name: b.order || this.state.order?.name || '' }) }),
        this.branchOnly && this.state.branch ? h('p', { class: 'returns-portal__meta', text: this.t('branchLine', { name: this.state.branch.name }) }) : null,
        h('ul', { class: 'returns-portal__branch-items', role: 'list' },
          (b.items || []).map((it) => h('li', { text: [it.title, this.metaLine(it.size, it.color)].filter(Boolean).join(' · ') }))));
    }

    announceIn(selector, message) {
      const box = this.$(selector);
      box.textContent = message;
      box.hidden = false;
      this.announce(message);
    }

    fail(message) {
      const box = this.$('[data-error]');
      box.textContent = message;
      box.hidden = false;
      this.announce(message);
    }

    unit(key) { return this.state.units.find((u) => u.unit_key === key); }

    // ---------------- units ----------------

    renderUnits() {
      const { order, windows, units, picks } = this.state;
      if (!order) return;
      this.$('[data-order-line]').textContent = this.t('orderLine', { name: order.name });

      const note = this.$('[data-window-note]');
      const date2 = fmtDate(windows.exchange_until);
      // The windows run from the day the order reached the customer (or the branch), said first.
      const arrived = windows.arrived_on
        ? this.t(windows.arrived_at === 'branch' ? 'arrivedBranch' : 'arrivedCourier', { date: fmtDate(windows.arrived_on) })
        : '';
      let text;
      if (this.branchOnly) text = this.t(windows.refund ? 'qrRefundUntil' : 'qrRefundClosed', { date: fmtDate(windows.refund_until) });
      else if (!windows.exchange) text = this.t('exchangeClosed');
      else if (windows.refund) text = this.t('refundUntil', { date: fmtDate(windows.refund_until), date2 });
      else text = this.t('exchangeOnlyUntil', { date2 });
      note.textContent = [arrived, text].filter(Boolean).join(' ');
      note.hidden = false;

      const pending = this.state.pending;
      this.$('[data-pending]').hidden = !pending;
      if (pending) {
        this.$('[data-pending-text]').textContent = this.t('pendingText', { amount: money(pending.pay_now) });
        this.$('[data-pending-pay]').href = pending.invoice_url;
      }

      // At the branch only money returns: the refund window decides.
      const open = this.branchOnly ? windows.refund : windows.exchange || windows.refund;
      this.$('[data-units-empty]').hidden = units.length > 0 || !!pending;
      this.$('[data-units-hint]').hidden = units.length === 0 || !open;
      // Tick every unit to return or exchange; the next screens walk them one by one.
      this.$('[data-units]').replaceChildren(...units.map((u) => {
        const on = open && this.state.selected.has(u.unit_key);
        const pick = picks.get(u.unit_key);
        return h('li', {},
          h('label', { class: `returns-portal__unit returns-portal__unit--select${on ? ' is-picked' : ''}` },
            h('input', {
              type: 'checkbox',
              class: 'returns-portal__check',
              'data-select': u.unit_key,
              'aria-label': [u.title, this.metaLine(u.size, u.color)].filter(Boolean).join(', '),
              checked: on,
              disabled: !open,
            }),
            this.thumb(u.image, u.title),
            h('div', {},
              h('p', { class: 'returns-portal__name', text: u.title }),
              h('p', { class: 'returns-portal__meta' }, this.metaLine(u.size, u.color)),
              h('p', { class: 'returns-portal__meta' }, `${this.t('paid')}: `, h('span', { dir: 'ltr', text: money(u.paid_value) })),
              on && pick ? h('p', { class: 'returns-portal__pick', text: this.pickLine(pick) }) : null)));
      }));

      const branch = this.state.branchPending;
      this.$('[data-branch-pending]').hidden = branch.length === 0;
      this.$('[data-branch-list]').replaceChildren(...branch.map((b) => h('div', {},
        this.branchCard(b),
        h('button', { type: 'button', class: 'button button--secondary', 'data-branch-cancel': b.return_id, text: this.t('branchCancel') }))));

      const history = this.state.history;
      this.$('[data-history]').hidden = history.length === 0;
      this.$('[data-history-list]').replaceChildren(...history.map((d) => {
        const key = { exchange: 'doneExchange', return: 'doneReturn', branch: 'doneBranch' }[d.action] || 'doneUnknown';
        return h('li', { class: 'returns-portal__unit is-done' },
          this.thumb(d.image, d.title),
          h('div', {},
            h('p', { class: 'returns-portal__name', text: d.title }),
            h('p', { class: 'returns-portal__meta' }, this.metaLine(d.size, d.color)),
            h('p', { class: 'returns-portal__pick', text: this.t(key, { date: fmtDate(d.date) }) })));
      }));

      this.updateUnitsButton();
    }

    updateUnitsButton() {
      const n = this.state.selected.size;
      // The branch QR page: the customer also confirms the items' condition (owner, 2026-10-29).
      const condition = this.$('[data-condition]');
      this.$('[data-start-wizard]').disabled = n === 0 || (!!condition && !condition.checked);
      if (n) this.announce(this.t('selected', { n: String(n) }));
    }

    thumb(src, alt) {
      const url = sizedImage(src, 192);
      return url
        ? h('img', { class: 'returns-portal__thumb', src: url, alt: alt || '', width: 80, height: 80, loading: 'lazy' })
        : h('span', { class: 'returns-portal__thumb', 'aria-hidden': 'true' });
    }

    metaLine(size, color) {
      const parts = [];
      if (size) parts.push(`${this.t('size')}: ${size}`);
      if (color) parts.push(`${this.t('color')}: ${color}`);
      return parts.join(' · ');
    }

    pickLine(pick) {
      if (pick.action === 'return') return this.t('willReturn');
      if (pick.action === 'other') return this.t('willOther');
      const tg = pick.target;
      return `${this.t('willExchange')}: ${tg.title}, ${this.metaLine(tg.size, tg.color)}`;
    }

    // ---------------- the ticked units, one by one ----------------

    // Reason, then return or exchange, for each ticked unit in turn; then the exchange
    // targets for all the exchanged ones on one screen (a tab per unit); then the summary.
    startWizard() {
      const keys = this.state.units.map((u) => u.unit_key).filter((k) => this.state.selected.has(k));
      if (!keys.length) return;
      for (const k of [...this.state.picks.keys()]) if (!keys.includes(k)) this.state.picks.delete(k);
      this.state.wizard = keys;
      this.openUnitAt(0, 'reason');
    }

    unitHead(u) {
      const n = this.state.wizard.length;
      const i = this.state.wizard.indexOf(u.unit_key);
      this.$('[data-unit-head]').replaceChildren(
        this.thumb(u.image, u.title),
        h('div', {},
          n > 1 && i >= 0 ? h('p', { class: 'returns-portal__progress', text: this.t('itemOf', { i: String(i + 1), n: String(n) }) }) : null,
          h('p', { class: 'returns-portal__name', text: u.title }),
          h('p', { class: 'returns-portal__meta' }, this.metaLine(u.size, u.color)),
          h('p', { class: 'returns-portal__meta' }, `${this.t('paid')}: `, h('span', { dir: 'ltr', text: money(u.paid_value) }))));
    }

    openUnitAt(index, stepName) {
      const key = this.state.wizard[index];
      const u = this.unit(key);
      if (!u) return;
      const pick = this.state.picks.get(key);
      this.state.wizIndex = index;
      this.state.unitKey = key;
      this.state.draft = { reason: pick?.reason ?? null, action: pick?.action ?? null, target: pick?.target ?? null };
      this.unitHead(u);
      this.$('[data-reasons]').replaceChildren(...this.strings.reasons.map((r) =>
        h('button', { type: 'button', class: 'returns-portal__choice', 'data-reason': r, 'aria-pressed': String(this.state.draft.reason === r), text: r })));
      if (stepName === 'action' && this.state.draft.reason) this.showActions(); else this.step('reason');
      this.show('unit');
    }

    step(name) {
      this.state.step = name;
      this.$$('[data-step]').forEach((s) => { s.hidden = s.dataset.step !== name; });
      this.$('[data-targets-next]').hidden = name !== 'exchange';
    }

    onReason(reason) {
      this.state.draft.reason = reason;
      this.$$('[data-reason]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.reason === reason)));
      this.showActions();
    }

    showActions() {
      const { windows, draft } = this.state;
      this.$('[data-refund-closed]').hidden = windows.refund;
      this.$('[data-action="return"]').hidden = !windows.refund;
      this.$('[data-action="exchange"]').hidden = !windows.exchange;
      const current = draft.action === 'return' ? 'return' : draft.action ? 'exchange' : null;
      this.$$('[data-action]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.action === current)));
      this.step('action');
    }

    // Return: done with this unit. Exchange: its target is chosen after the last unit
    // (a size, colour or "another product" chosen earlier is kept).
    onAction(action) {
      const { draft } = this.state;
      if (action === 'return') { draft.action = 'return'; draft.target = null; }
      else if (draft.action !== 'other' && draft.action !== 'exchange') { draft.action = 'exchange'; draft.target = null; }
      this.state.picks.set(this.state.unitKey, { ...draft });
      const next = this.state.wizIndex + 1;
      if (next < this.state.wizard.length) return this.openUnitAt(next, 'reason');
      // The last unit is an exchange: its choice screen, even when a size was chosen before
      // (going back and choosing "exchange" again never skips to the summary).
      if (draft.action !== 'return') return this.openTargets(this.state.unitKey);
      this.nextTarget();
    }

    // The exchanged units, and whether each one has its target yet.
    exchangeKeys() {
      return this.state.wizard.filter((k) => ['exchange', 'other'].includes(this.state.picks.get(k)?.action));
    }

    resolved(key) {
      const p = this.state.picks.get(key);
      return p?.action === 'other' || (p?.action === 'exchange' && !!p.target);
    }

    // The next exchanged unit still without a target, or the summary once there is none.
    nextTarget() {
      const open = this.exchangeKeys().find((k) => !this.resolved(k));
      if (open) return this.openTargets(open);
      this.proceed();
    }

    // Every unit has its choice: the summary, or, when any unit goes for another product,
    // straight to the site to fill the cart (the summary comes after the cart).
    proceed() {
      if ([...this.state.picks.values()].some((p) => p.action === 'other')) return this.startRx();
      this.go('summary');
    }

    // One screen for every exchanged unit: a tab per unit (when there are several), and under
    // it the same size / colour / another-product tabs as before.
    openTargets(key) {
      const u = this.unit(key);
      if (!u) return;
      const keys = this.exchangeKeys();
      const pick = this.state.picks.get(key) || {};
      this.state.unitKey = key;
      this.state.draft = { reason: pick.reason ?? null, action: pick.action ?? 'exchange', target: pick.target ?? null };
      this.state.targets.clear();
      this.unitHead(u);
      this.$('[data-product-tabs]').hidden = keys.length < 2;
      this.refreshTargets();
      this.step('exchange');
      this.switchTab(this.state.draft.action === 'other' ? 'other' : (this.state.draft.target?.kind || 'size'));
      this.loadOffers(u);
      this.show('unit');
    }

    // Back one step: targets -> the last unit's action; action -> reason; reason -> the
    // previous unit's action, or the units list from the first one.
    unitBack() {
      if (this.state.step === 'exchange') return this.openUnitAt(this.state.wizard.length - 1, 'action');
      if (this.state.step === 'action') return this.step('reason');
      if (this.state.wizIndex > 0) return this.openUnitAt(this.state.wizIndex - 1, 'action');
      this.go('units');
    }

    summaryBack() {
      const keys = this.exchangeKeys();
      if (keys.length) return this.openTargets(keys[keys.length - 1]);
      if (this.state.wizard.length) return this.openUnitAt(this.state.wizard.length - 1, 'action');
      this.go('units');
    }

    // "Another product" for this unit: first a short explanation (the cart may hold more than
    // the exchange), then straight on to the site, without the summary in between.
    openRxDialog() {
      this.$('[data-rx-error]').hidden = true;
      this.$('[data-rx-dialog]').showModal();
    }

    async rxGo() {
      this.state.draft = { ...this.state.draft, action: 'other', target: null };
      this.state.picks.set(this.state.unitKey, { ...this.state.draft });
      this.$$('[data-pick]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      this.refreshTargets();
      // Another exchanged unit still needs its choice: that one first, the site after it.
      const open = this.exchangeKeys().find((k) => !this.resolved(k));
      if (open) {
        this.$('[data-rx-dialog]').close();
        return this.openTargets(open);
      }
      await this.startRx();
    }

    // The unit chips (a check on each unit that has its exchange), the line saying what was
    // chosen for the current unit, and the button: next unit while any is still open, else
    // the summary. It only works once the current unit has its choice.
    refreshTargets() {
      const keys = this.exchangeKeys();
      const key = this.state.unitKey;
      this.$('[data-product-tabs]').replaceChildren(...keys.map((k) => {
        const it = this.unit(k);
        return h('button', {
          type: 'button',
          class: 'returns-portal__tab returns-portal__product-tab',
          role: 'tab',
          'data-product-tab': k,
          'aria-selected': String(k === key),
        },
        h('span', { text: [it.title, it.size ? `${this.t('size')} ${it.size}` : null].filter(Boolean).join(' · ') }),
        this.resolved(k) ? h('span', { class: 'returns-portal__done-mark', 'aria-label': this.t('tabDone'), text: '✓' }) : null);
      }));
      const line = this.$('[data-selection]');
      const text = this.choiceText(this.state.picks.get(key));
      line.textContent = text;
      line.hidden = !text;
      if (text) this.announce(text);
      const next = this.$('[data-targets-next]');
      next.disabled = !this.resolved(key);
      const anyOther = [...this.state.picks.values()].some((p) => p.action === 'other');
      next.querySelector('[data-label]').textContent = keys.some((k) => k !== key && !this.resolved(k)) ? this.t('nextItem')
        : this.t(anyOther ? 'toSite' : 'toSummary');
    }

    // What the customer chose for one unit, in words.
    choiceText(pick) {
      if (pick?.action === 'other') return this.t('chosenOther');
      const tg = pick?.action === 'exchange' ? pick.target : null;
      if (!tg) return '';
      const what = tg.kind === 'color'
        ? `${this.t('color')} ${tg.color}, ${this.t('size')} ${tg.size}`
        : `${this.t('size')} ${tg.size}`;
      return this.t('chosen', { what });
    }

    targetsNext() {
      const key = this.state.unitKey;
      const open = this.exchangeKeys().find((k) => k !== key && !this.resolved(k));
      if (open) return this.openTargets(open);
      if (this.resolved(key)) this.proceed();
    }

    switchTab(name) {
      this.$$('[data-tab]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.tab === name)));
      this.$$('[data-panel]').forEach((p) => { p.hidden = p.dataset.panel !== name; });
    }

    // ---------------- offers (sizes, colours) ----------------

    parseSection(html, attribute) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const script = doc.querySelector(`script[${attribute}]`);
      if (!script) throw new Error('section data missing');
      return JSON.parse(script.textContent);
    }

    async loadOffers(u) {
      const panels = ['size', 'color'].map((n) => this.$(`[data-panel="${n}"]`));
      panels.forEach((p) => p.replaceChildren(h('p', { class: 'returns-portal__muted', text: this.t('loading') })));
      if (!u?.product_handle) {
        panels.forEach((p) => p.replaceChildren(h('p', { class: 'returns-portal__muted', text: this.t('noSizes') })));
        return;
      }
      let data = this.state.offers.get(u.product_handle);
      if (!data) {
        try {
          const res = await fetch(`${this.root}products/${encodeURIComponent(u.product_handle)}?section_id=returns-offers`, { signal: this.abort.signal });
          if (!res.ok) throw new Error(String(res.status));
          data = this.parseSection(await res.text(), 'data-returns-offers');
          this.state.offers.set(u.product_handle, data);
        } catch (e) {
          if (e.name === 'AbortError') return;
          panels.forEach((p) => p.replaceChildren(h('p', { class: 'returns-portal__error', text: this.t('loadError') })));
          return;
        }
      }
      if (this.state.unitKey !== u.unit_key) return; // the customer moved on meanwhile

      this.$('[data-panel="size"]').replaceChildren(data.product
        ? this.offerRow(u, data.product, 'size')
        : h('p', { class: 'returns-portal__muted', text: this.t('noSizes') }));

      this.$('[data-panel="color"]').replaceChildren(...(data.siblings.length
        ? data.siblings.map((p) => this.offerRow(u, p, 'color'))
        : [h('p', { class: 'returns-portal__muted', text: this.t('noColors') })]));
    }

    // What this customer pays for a variant: its price. The server prices with the same
    // rule; this is only what the card shows.
    priceFor(product, v) {
      return { price: v.price };
    }

    // One product with its in-stock sizes as buttons.
    offerRow(u, product, kind) {
      const chosen = this.state.draft?.target;
      const buttons = product.variants.map((v) => {
        const id = String(++this.state.targetSeq);
        const eff = this.priceFor(product, v);
        this.state.targets.set(id, {
          kind,
          variant_id: v.id,
          sku: v.sku,
          size: v.size,
          color: product.color,
          title: product.title,
          image: product.image,
          price: eff.price,
        });
        const same = v.size != null && u.size != null && String(v.size).trim() === String(u.size).trim();
        // "Another size" never offers the size the customer already has: shown greyed, not a
        // button that does anything. In "another colour" that size is a real choice.
        const own = same && kind === 'size';
        return h('button', {
          type: 'button',
          class: `returns-portal__size${own ? ' is-same' : ''}`,
          'data-pick': id,
          'aria-pressed': String(chosen?.variant_id === v.id),
          'aria-label': same ? `${v.size} (${this.t('sameSize')})` : v.size,
          title: own ? this.t('sameSize') : null,
          disabled: own,
          dir: 'ltr',
          text: v.size ?? '',
        });
      });
      const first = product.variants[0] ? this.priceFor(product, product.variants[0]) : null;
      return h('div', { class: 'returns-portal__offer' },
        this.thumb(product.image, product.title),
        h('div', {},
          h('p', { class: 'returns-portal__name', text: kind === 'size' ? this.metaLine(null, product.color) || product.title : product.title }),
          kind === 'size' ? null : h('p', { class: 'returns-portal__meta', text: this.metaLine(null, product.color) }),
          first ? h('p', { class: 'returns-portal__price' },
            h('span', { dir: 'ltr', text: fromAgorot(first.price) })) : null,
          h('div', { class: 'returns-portal__sizes' }, buttons)));
    }

    // A size or colour for this unit. The screen stays on it: the button turns dark and the
    // line below says what was chosen.
    onPick(id) {
      const target = this.state.targets.get(id);
      if (!target) return;
      this.state.draft = { ...this.state.draft, action: 'exchange', target };
      this.state.picks.set(this.state.unitKey, { ...this.state.draft });
      this.$$('[data-pick]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.pick === id)));
      this.refreshTargets();
    }

    // ---------------- summary ----------------

    picksPayload() {
      return [...this.state.picks].map(([key, pick]) => ({
        unit_key: key,
        reason: pick.reason,
        action: pick.action,
        target_variant_id: pick.target?.variant_id != null ? String(pick.target.variant_id) : null,
      }));
    }

    // The request as columns: what goes back for money, what goes back in an exchange, and the
    // new items on their way. Only the columns this request has (a plain return is one column);
    // side by side on a computer, one under the other on a phone.
    renderBoard({ returned = [], exchanged = [], incoming = [], incomingLater = false }) {
      const card = (it, ...extra) => h('li', { class: 'returns-portal__board-item' },
        this.thumb(it.image, it.title),
        h('div', {},
          h('p', { class: 'returns-portal__name', text: it.title }),
          h('p', { class: 'returns-portal__meta' }, this.metaLine(it.size, it.color)),
          ...extra));
      const reason = (it) => (it.reason ? h('p', { class: 'returns-portal__meta returns-portal__reason', text: it.reason }) : null);
      const price = (text) => (text ? h('p', { class: 'returns-portal__board-price' },
        h('span', { dir: 'ltr', text })) : null);
      const column = (kind, items, later) => h('section', { class: `returns-portal__col returns-portal__col--${kind.toLowerCase()}` },
        h('header', { class: 'returns-portal__col-head' },
          h('h3', { class: 'returns-portal__col-title' },
            h('span', { text: this.t(`col${kind}`) }),
            items.length ? h('span', { class: 'returns-portal__col-count', text: String(items.length) }) : null),
          h('p', { class: 'returns-portal__col-sub', text: this.t(`col${kind}Sub`) })),
        items.length ? h('ul', { class: 'returns-portal__col-list', role: 'list' }, items)
          : h('p', { class: 'returns-portal__muted', text: later }));

      const cols = [];
      if (returned.length) cols.push(column('Return', returned.map((u) => card(u, reason(u), price(u.paidText)))));
      if (exchanged.length) cols.push(column('Exchange', exchanged.map((u) => card(u, reason(u)))));
      if (incoming.length || incomingLater) {
        cols.push(column('New', incoming.map((l) => card(l, price(l.priceText))), this.t('colNewLater')));
      }
      const board = this.$('[data-summary]');
      board.style.setProperty('--cols', String(Math.max(cols.length, 1)));
      board.replaceChildren(...cols);
    }

    // The board from the customer's own choices, before the server has priced anything.
    boardFromPicks() {
      const entries = [...this.state.picks].map(([key, pick]) => ({ u: this.unit(key), pick })).filter((e) => e.u);
      const unitCard = ({ u, pick }) => ({ ...u, reason: pick.reason, paidText: money(u.paid_value) });
      this.renderBoard({
        returned: entries.filter((e) => e.pick.action === 'return').map(unitCard),
        exchanged: entries.filter((e) => e.pick.action !== 'return').map(unitCard),
        incoming: entries.filter((e) => e.pick.action === 'exchange' && e.pick.target).map(({ pick }) => ({
          title: pick.target.title, size: pick.target.size, color: pick.target.color, image: pick.target.image,
          priceText: fromAgorot(pick.target.price),
        })),
        incomingLater: entries.some((e) => e.pick.action === 'other'),
      });
    }

    // The board from the server's quote: its units and its new lines, with its prices.
    boardFromQuote(q) {
      const units = q.units || [];
      if (!units.length) return this.boardFromPicks();
      const unitCard = (u) => ({ ...u, paidText: u.paid != null ? money(u.paid) : null });
      this.renderBoard({
        returned: units.filter((u) => u.action === 'return').map(unitCard),
        exchanged: units.filter((u) => u.action !== 'return').map(unitCard),
        incoming: (q.lines || []).map((l) => ({ title: l.title, size: l.size, color: l.color, image: l.image, priceText: money(l.price) })),
      });
    }

    resetSummary() {
      this.state.quoteOk = false;
      this.$('[data-totals]').hidden = true;
      this.$('[data-summary-error]').hidden = true;
      this.$('[data-summary-note]').hidden = true;
      this.$('[data-start-rx]').hidden = true;
      this.$('[data-submit]').hidden = false;
      this.$('[data-back-shop]').hidden = true;
      this.$('[data-summary-back]').hidden = false;
      this.updateButtons();
    }

    renderSummary() {
      this.resetSummary();
      this.boardFromPicks();

      // Branch returns have their own page (the QR code in the branches), so this page always
      // sends a courier and the "how will you return it" row stays hidden.
      this.state.method = 'courier';
      this.$('[data-method-row]').hidden = true;

      if ([...this.state.picks.values()].some((p) => p.action === 'other')) {
        // "Another product": the amount can only be computed once the cart is filled.
        this.note(this.t('otherNote'));
        this.$('[data-submit]').hidden = true;
        this.$('[data-start-rx]').hidden = false;
        this.updateButtons();
        return;
      }
      this.quote({ mode: 'quote', ...this.state.auth, units: this.picksPayload(), method: this.state.method });
    }

    note(text) {
      const n = this.$('[data-summary-note]');
      n.textContent = text;
      n.hidden = false;
    }

    summaryError(code) {
      const box = this.$('[data-summary-error]');
      box.textContent = this.errText(code);
      box.hidden = false;
      this.announce(box.textContent);
    }

    async quote(payload) {
      const seq = ++this.state.quoteSeq;
      this.note(this.t('calculating'));
      try {
        const data = await this.post(this.submitUrl, payload);
        if (seq !== this.state.quoteSeq) return;  // a newer quote is on its way
        this.$('[data-summary-note]').hidden = true;
        if (!data.ok) return this.summaryError(data.error);
        this.renderQuote(data.quote);
        this.state.payNow = data.quote.pay_now;
        this.state.quoteOk = true;
        this.updateButtons();
      } catch (e) {
        if (e.name !== 'AbortError') this.summaryError('server_error');
      }
    }

    // The amounts are the server's, never recomputed here.
    renderQuote(q) {
      this.boardFromQuote(q);
      const credit = (Number(q.items_total) - Number(q.pay_items)).toFixed(2);
      const row = (label, value, strong, minus) => [
        h('dt', { class: strong ? 'is-total' : null, text: label }),
        h('dd', { class: strong ? 'is-total' : null }, h('span', { dir: 'ltr', text: minus && Number(value) > 0 ? negative(value) : money(value) })),
      ];
      const dl = this.$('[data-totals]');
      dl.replaceChildren(
        ...(q.lines.length ? [...row(this.t('rowItems'), q.items_total), ...row(this.t('rowCredit'), credit, false, true), ...row(this.t('rowDiff'), q.pay_items)] : []),
        ...row(this.t('rowFee'), q.fee),
        ...row(this.t('rowTotal'), q.pay_now, true),
        ...(Number(q.refund_later) > 0 ? row(this.t(q.method === 'branch' ? 'rowRefundBranch' : 'rowRefund'), q.refund_later) : []),
      );
      dl.hidden = false;
    }

    updateButtons() {
      const declared = this.$('[data-declare]').checked;
      this.$('[data-submit]').disabled = !(declared && this.state.quoteOk) || this.state.busy;
      this.$('[data-start-rx]').disabled = !declared || this.state.busy;
    }

    // ---------------- "another product": browse the site ----------------

    // Called from the "another product" popup, from the targets screen, or from the summary:
    // a failure is shown where the customer is.
    async startRx() {
      const fail = (code) => {
        const dialog = this.$('[data-rx-dialog]');
        if (dialog.open) return this.announceIn('[data-rx-error]', this.errText(code));
        if (!this.$('[data-screen="unit"]').hidden) return this.announceIn('[data-unit-error]', this.errText(code));
        this.summaryError(code);
      };
      const rx = window.WeShoesRx;
      if (!rx) return fail('server_error');
      this.$('[data-unit-error]').hidden = true;
      const go = this.$('[data-rx-go]');
      const next = this.$('[data-targets-next]');
      go.disabled = true;
      next.disabled = true;
      this.state.busy = true;
      this.updateButtons();
      try {
        const data = await this.post(this.submitUrl, { mode: 'token', ...this.state.auth, units: this.picksPayload() });
        if (!data.ok) return fail(data.error);
        const snapshot = await rx.saveAndClearCart();
        rx.write({ token: data.token, exp: Date.parse(data.expires_at), back: window.location.pathname + window.location.search, snapshot });
        window.location.assign(this.root);
      } catch (e) {
        if (e.name !== 'AbortError') fail('server_error');
      } finally {
        go.disabled = false;
        next.disabled = !this.resolved(this.state.unitKey);
        this.state.busy = false;
        this.updateButtons();
      }
    }

    // Back from the cart: rebuild the request from the token and price the cart.
    async rxSummary() {
      const session = window.WeShoesRx.read();
      this.show('summary');
      this.resetSummary();
      this.$('[data-summary-back]').hidden = true;
      this.$('[data-back-shop]').hidden = false;
      this.$('[data-summary]').replaceChildren();
      let cart;
      try {
        const res = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, signal: this.abort.signal });
        cart = await res.json();
      } catch (e) {
        if (e.name !== 'AbortError') this.summaryError('server_error');
        return;
      }
      const lines = (cart.items || []).map((i) => ({ variant_id: String(i.variant_id), quantity: i.quantity }));
      if (!lines.length) return this.note(this.t('rxEmpty'));
      this.state.rx = { token: session.token, cart: lines };

      this.note(this.t('calculating'));
      try {
        const data = await this.post(this.submitUrl, { mode: 'quote', token: session.token, cart: lines });
        this.$('[data-summary-note]').hidden = true;
        if (!data.ok) return this.summaryError(data.error);
        this.renderQuote(data.quote);
        this.state.payNow = data.quote.pay_now;
        this.state.quoteOk = true;
        this.updateButtons();
      } catch (e) {
        if (e.name !== 'AbortError') this.summaryError('server_error');
      }
    }

    // ---------------- the branch QR page: confirm and get the code ----------------

    // Every ticked unit returned for money at the branch, no reason asked; the server opens the
    // Return and answers with the 6-digit code, shown on the done screen.
    async submitBranch(button) {
      const keys = this.state.units.map((u) => u.unit_key).filter((k) => this.state.selected.has(k));
      if (!keys.length || this.state.busy) return;
      const error = this.$('[data-units-error]');
      error.hidden = true;
      this.state.picks.clear();
      for (const k of keys) this.state.picks.set(k, { reason: QR_REASON, action: 'return', target: null });
      this.state.method = 'branch';
      this.state.busy = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        const data = await this.post(this.submitUrl, {
          mode: 'submit', ...this.state.auth, units: this.picksPayload(), method: 'branch', expected_pay_now: '0.00',
          branch_number: this.state.branch?.number,
        });
        if (!data.ok || data.method !== 'branch') return this.announceIn('[data-units-error]', this.errText(data.error || 'server_error'));
        this.showBranchDone(data, 'qrDoneTitle', 'qrDoneText');
      } catch (e) {
        if (e.name !== 'AbortError') this.announceIn('[data-units-error]', this.t('errServer'));
      } finally {
        this.state.busy = false;
        button.removeAttribute('aria-busy');
        this.updateUnitsButton();
      }
    }

    // After the code is shown: wait for the cashier. A spinning wheel and a red line until the
    // branch app approves the code, then "done" (owner, 2026-10-29). Asks returns-submit every
    // 8 seconds while the page is visible, for up to an hour.
    waitForBranch(returnId) {
      const box = this.$('[data-branch-wait]');
      if (!box || !returnId) return;
      clearTimeout(this.waitTimer);
      const spinner = this.$('[data-branch-wait-spinner]');
      const text = this.$('[data-branch-wait-text]');
      const set = (state, key) => {
        box.dataset.state = state;
        text.textContent = this.t(key);
        spinner.hidden = state !== 'waiting';
        box.hidden = false;
        // Once the cashier approved there is nothing to go back to.
        if (state === 'done') {
          const back = this.$('[data-branch-back]');
          if (back) back.hidden = true;
          this.$('[data-branch-back-dialog]')?.close();
        }
      };
      set('waiting', 'waitCashier');
      const until = Date.now() + 60 * 60_000;
      const tick = async () => {
        if (Date.now() > until) return set('stopped', 'waitStopped');
        if (!document.hidden) {
          try {
            const data = await this.post(this.submitUrl, { mode: 'status', ...this.state.auth, return_id: returnId });
            if (data.ok && data.state === 'refunded') return set('done', 'waitDone');
            if (data.ok && data.state === 'approved') return set('done', 'waitApproved');
            // An older server without the status mode: keep the line, stop asking.
            if (!data.ok && data.error === 'bad_mode') return;
          } catch (e) {
            if (e.name === 'AbortError') return;
          }
        }
        this.waitTimer = setTimeout(tick, 8000);
      };
      this.waitTimer = setTimeout(tick, 8000);
    }

    // A branch return was opened: the code for the cashier, the order and the items.
    showBranchDone(data, titleKey, textKey) {
      this.$('[data-done-title]').textContent = this.t(titleKey);
      this.$('[data-done-text]').textContent = this.t(textKey);
      const items = [...this.state.picks.keys()].map((key) => this.unit(key)).filter(Boolean)
        .map((u) => ({ title: u.title, size: u.size, color: u.color }));
      this.$('[data-done-branch]').replaceChildren(this.branchCard({ ...data.branch, return_id: data.return_id, items }));
      this.$('[data-done-branch]').hidden = false;
      this.state.doneReturnId = data.return_id;
      const back = this.$('[data-branch-back]');
      if (back) back.hidden = !this.branchOnly;
      this.show('done');
      if (this.branchOnly) this.waitForBranch(data.return_id);
    }

    // ---------------- submit ----------------

    async submit() {
      this.state.busy = true;
      this.updateButtons();
      try {
        const payload = this.state.rx
          ? { mode: 'submit', token: this.state.rx.token, cart: this.state.rx.cart, expected_pay_now: this.state.payNow }
          : { mode: 'submit', ...this.state.auth, units: this.picksPayload(), method: this.state.method, expected_pay_now: this.state.payNow };
        const data = await this.post(this.submitUrl, payload);
        if (!data.ok && data.error === 'pending_request_exists' && data.detail?.invoice_url) {
          // Already sent (e.g. a double tap): go on to that request's payment.
          data.ok = true;
          data.invoice_url = data.detail.invoice_url;
          data.pay_now = data.detail.pay_now;
        }
        if (!data.ok && data.error === 'quote_changed') {
          // Prices moved since the summary: show the new amount and ask again.
          if (this.state.rx) await this.rxSummary(); else this.renderSummary();
          return this.summaryError('quote_changed');
        }
        if (!data.ok) return this.summaryError(data.error);

        // A branch return: nothing to pay. Show what to bring and what to show the cashier.
        if (data.method === 'branch') return this.showBranchDone(data, 'doneBranchTitle', 'doneBranchText');

        // After browsing: give the customer their own cart back before paying.
        if (this.state.rx && window.WeShoesRx) await window.WeShoesRx.end();

        this.$('[data-done-title]').textContent = this.t('doneTitle');
        this.$('[data-done-text]').textContent = this.t('donePay', { amount: money(data.pay_now) });
        const link = this.$('[data-pay-link]');
        if (data.invoice_url) {
          link.href = data.invoice_url;
          link.hidden = false;
        }
        this.show('done');
        if (data.invoice_url) setTimeout(() => window.location.assign(data.invoice_url), 1200);
      } catch (e) {
        if (e.name !== 'AbortError') this.summaryError('server_error');
      } finally {
        this.state.busy = false;
        this.updateButtons();
      }
    }
  }

  customElements.define('returns-portal', ReturnsPortal);
})();
